/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { Else, If, Then, When } from "react-if";
import { Box, ExtendedTooltip, Spacer, Text, theme } from "@nordcloud/gnui";
import { TooltipText } from "~/components";
import { dateFormat } from "~/constants";
import { convertMinutesToTime } from "~/views/events/helpers";

type Props = {
  timeDetails: {
    scheduleTimezone?: string;
    startTime?: string;
    estimatedEndTime?: string;
    endTime?: string;
  };
  isEventFinished?: boolean;
};

export function TimeDetails({ timeDetails, isEventFinished }: Props) {
  const startTimeTZ = dayjs(timeDetails?.startTime ?? "")
    .tz(timeDetails?.scheduleTimezone ?? "")
    .format(dateFormat.dayMonthYearHourMinute);

  const endTime = timeDetails?.endTime ?? timeDetails?.estimatedEndTime ?? "";

  const endTimeTZ = dayjs(endTime)
    .tz(timeDetails?.scheduleTimezone ?? "")
    .format(dateFormat.dayMonthYearHourMinute);

  const duration = dayjs(endTime)
    .startOf("minutes")
    .diff(dayjs(timeDetails?.startTime).startOf("minutes"), "minutes");

  return (
    <Box boxStyle="lightGrey" css={{ flex: 1 }}>
      <div data-testid="event-start-time">
        <Text tag="div" size="sm" color={theme.color.text.text02}>
          Start Time (local time)
        </Text>
        <ExtendedTooltip
          caption={
            <>
              Event Time: {startTimeTZ}
              <br />
              Timezone: {timeDetails?.scheduleTimezone}
            </>
          }
          hideTimeout={0}
        >
          <TooltipText
            size="md"
            tag="span"
            mr={theme.spacing.spacing01}
            weight="regular"
            color={theme.color.text.text01}
            css={{ textDecorationStyle: "initial" }}
          >
            {dayjs(timeDetails?.startTime).format(
              dateFormat.dayMonthYearHourMinute
            )}
          </TooltipText>
        </ExtendedTooltip>
      </div>
      <Spacer height={theme.spacing.spacing04} />
      <div data-testid="event-end-time">
        <Text tag="div" size="sm" color={theme.color.text.text02}>
          End Time (local time)
        </Text>
        <ExtendedTooltip
          caption={
            <If condition={isEventFinished}>
              <Then>
                <>
                  Event Time: {endTimeTZ}
                  <br />
                  Timezone: {timeDetails?.scheduleTimezone}
                  <br />
                </>
              </Then>
              <Else>End Time will appear when the event is complete</Else>
            </If>
          }
          hideTimeout={0}
        >
          <TooltipText
            size="md"
            tag="span"
            mr={theme.spacing.spacing01}
            weight="regular"
            color={theme.color.text.text01}
            css={{ textDecorationStyle: "initial" }}
          >
            <If condition={isEventFinished}>
              <Then>
                {dayjs(endTime).format(dateFormat.dayMonthYearHourMinute)}
              </Then>
              <Else>Awaiting completion</Else>
            </If>
          </TooltipText>
        </ExtendedTooltip>
      </div>
      <Spacer height={theme.spacing.spacing04} />
      <When condition={isEventFinished}>
        <div data-testid="event-duration">
          <Text tag="div" size="sm" color={theme.color.text.text02}>
            Event Duration
          </Text>
          <Text tag="div">{convertMinutesToTime(duration)}</Text>
        </div>
      </When>
    </Box>
  );
}
